import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import {
  FaPlus, FaCheck, FaEdit, FaTrash, FaGasPump,
  FaUserTie, FaReceipt, FaSpinner, FaChevronDown,
  FaTimes, FaFilter, FaAngleDoubleDown, FaUniversity, FaTools,
  FaToggleOn, FaToggleOff, FaUsers, FaChartBar, FaMagic,
  FaFileInvoiceDollar, FaExchangeAlt, FaExclamationCircle ,
} from 'react-icons/fa';
import ModalContasAPagar from './ModalContasAPagar';
import WizardLancamento from './WizardLancamento';
import ModalBaixaPagador from './ModalBaixaPagador'; // [PAGADOR]
import ModalGestaoPagadores from './ModalGestaoPagadores'; // [PAGADOR-ADMIN]
import ModalConsolidadoPagadores from './ModalConsolidadoPagadores'; // [PAGADOR]
import CapActionsMenu from './CapActionsMenu';
import './ContasAPagar.css';
import RegenteLancamento from './RegenteLancamento';
import { parseOSTarefa } from '../utils/osTarefa';
import ModalConversaoConta from './ModalConversaoConta';
import ModalAprovarConta from './ModalAprovarConta';

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmtCurrency = (v) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v ?? 0);

const fmtDate = (s) => {
  if (!s) return '—';
  const d = String(s).split('T')[0].split('-');
  return d.length === 3 ? `${d[2]}/${d[1]}/${d[0]}` : '—';
};

const monthRange = () => {
  const d = new Date();
  const y = d.getFullYear(), m = String(d.getMonth() + 1).padStart(2, '0');
  const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  return { start: `${y}-${m}-01`, end: `${y}-${m}-${last}` };
};

// Sentinela para o grupo "Sem categoria" (contas sem categoria_id)
const SEM_CATEGORIA = '__sem_categoria__';

// ─── Status config ───────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  pendente: { label: 'Pendente', cls: 'pill-pending' },
  vencido:  { label: 'Vencido',  cls: 'pill-overdue' },
  pago:     { label: 'Pago',     cls: 'pill-paid'    },
  aprovado: { label: 'Aprovado', cls: 'pill-approved' },
};

const OrigemBadge = ({ conta }) => {
  const { isTarefa, osId } = parseOSTarefa(conta.descricao);
  if (isTarefa) {
    return (
      <span className="origem-tag tarefa" title={`Gerado ao encerrar a OS #${osId}`}>
        <FaTools /> Tarefa OS
      </span>
    );
  }

  const isSupply =
    conta.origem_tipo === 'manutencao_frota' ||
    conta.descricao?.includes('Abastecimento Cartão');
  if (isSupply)
    return <span className="origem-tag fuel"><FaGasPump /> Abastecimento</span>;

  if (conta.forma_pagamento === 'conta_digital')
    return <span className="origem-tag baas"><FaUniversity /> Conta Digital</span>;

  const map = {
    folha_pagamento: { label: 'Folha',   cls: 'folha',   Icon: FaUserTie },
    despesa_manual:  { label: 'Despesa', cls: 'despesa', Icon: FaReceipt },
  };
  const cfg = map[conta.origem_tipo];
  if (!cfg) return <span className="origem-tag manual">Manual</span>;
  return <span className={`origem-tag ${cfg.cls}`}><cfg.Icon /> {cfg.label}</span>;
};

// ─── Component ───────────────────────────────────────────────────────────────
export default function ContasAPagar() {
  const range = monthRange();

  // ── Core state ──────────────────────────────────────────────────────────────
  const [kpis,         setKpis]         = useState({ total_pendente: 0, total_vencido: 0, vence_hoje: 0, pago_mes: 0 });
  const [contas,       setContas]       = useState([]);
  const [loadingKpi,   setLoadingKpi]   = useState(true);
  const [loadingTable, setLoadingTable] = useState(true);
  const [modalOpen,    setModalOpen]    = useState(false);
  const [editItem,     setEditItem]     = useState(null);
  const [selected,     setSelected]     = useState(new Set());
  const [submitting,   setSubmitting]   = useState(false);
  const [currentUser,  setCurrentUser]  = useState(null);
  const [filtersOpen,  setFiltersOpen]  = useState(false);
  const [wizardOpen,   setWizardOpen]   = useState(false);
  const [conversaoTarget, setConversaoTarget] = useState(null);
  const [aprovarModal, setAprovarModal] = useState({ open: false, contas: [] });

  // [PAGADOR] Toggle do tenant + lista de pagadores + estado da modal de baixa
  const [exibirPagador, setExibirPagador] = useState(false);
  const [pagadores,     setPagadores]     = useState([]);
  const [baixaModal,    setBaixaModal]    = useState({ open: false, contas: [] });
  // [PAGADOR-ADMIN] Gestão de pagadores + estado do toggle em transição
  const [gestaoOpen,  setGestaoOpen]  = useState(false);
  const [togglingPag, setTogglingPag] = useState(false);
  const [consolidadoOpen, setConsolidadoOpen] = useState(false); // [PAGADOR]

  const [categoriaFiltro, setCategoriaFiltro] = useState('');

  const [wallet,     setWallet]     = useState(null);
  const [payingBaas, setPayingBaas] = useState(false);

  // ── Filters ─────────────────────────────────────────────────────────────────
  const [filters, setFilters] = useState({
    dataInicio:  range.start,
    dataFim:     range.end,
    status:      '',
    origem:      '',
    pagador_id:  '',
    numero_nota: '',  // [NF]
  });

  // ── IA Search ────────────────────────────────────────────────────────────────
  const [iaBusca,   setIaBusca]   = useState('');
  const [iaLoading, setIaLoading] = useState(false);
  const [iaResult,  setIaResult]  = useState(null); // { ids, resumo, total_encontrado }
  const [iaAtivo,   setIaAtivo]   = useState(false);

  const debounceRef = useRef(null);

  // ── Init ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    try { setCurrentUser(JSON.parse(localStorage.getItem('user') || '{}')); } catch {}
  }, []);

  const isAdminGeral = currentUser?.perfil === 'admin_geral';
  const isFinanceiro = currentUser?.perfil === 'financeiro';

  // [PAGADOR] Carrega o estado do toggle e a lista de pagadores (uma vez no mount).
  const fetchPagadorSetup = useCallback(async () => {
    try {
      const [cfg, pag] = await Promise.all([
        api.get('/api/financeiro/config/exibir-pagador'),
        api.get('/api/financeiro/pagadores'),
      ]);
      setExibirPagador(!!cfg.data?.exibir_pagador);
      setPagadores(Array.isArray(pag.data) ? pag.data : []);
    } catch {
      setExibirPagador(false);
      setPagadores([]);
    }
  }, []);

  useEffect(() => { fetchPagadorSetup(); }, [fetchPagadorSetup]);

  // ── Fetch wallet BaaS (silencioso se não configurado) ──────────────────────
  const fetchWallet = useCallback(async () => {
    try {
      const { data } = await api.get('/api/baas/wallet');
      setWallet(data?.wallet?.status === 'ACTIVE' ? data.wallet : null);
    } catch {
      setWallet(null);
    }
  }, []);

  useEffect(() => { fetchWallet(); }, [fetchWallet]);

  // ── Fetch financeiro ─────────────────────────────────────────────────────────
  const fetchKpis = useCallback(async () => {
    setLoadingKpi(true);
    try {
      const { data } = await api.get('/api/financeiro/kpis-pagar', { params: filters });
      setKpis(data);
    } catch { toast.error('Erro ao carregar indicadores.'); }
    finally { setLoadingKpi(false); }
  }, [filters]);

  const fetchContas = useCallback(async () => {
    setLoadingTable(true);
    try {
      const res = await api.get('/api/contas-a-pagar', { params: filters });
      setContas(Array.isArray(res.data) ? res.data : (res.data?.data ?? []));
    } catch { toast.error('Erro ao carregar contas.'); }
    finally { setLoadingTable(false); }
  }, [filters]);

  const reload = useCallback(() => {
    fetchKpis();
    fetchContas();
    setSelected(new Set());
    fetchWallet();
  }, [fetchKpis, fetchContas, fetchWallet]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(reload, 350);
    return () => clearTimeout(debounceRef.current);
  }, [reload]);

  // ── IA Search ────────────────────────────────────────────────────────────────
  const handleIaBusca = async () => {
    if (!iaBusca.trim()) return;
    setIaLoading(true);
    try {
      const { data } = await api.post('/api/contas-a-pagar/ia-busca', { query: iaBusca });
      setIaResult(data);
      setIaAtivo(true);
      if (data.total_encontrado === 0) toast.info('IA não encontrou contas para essa busca.');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Erro na busca com IA.');
    } finally {
      setIaLoading(false);
    }
  };

  const clearIaBusca = () => {
    setIaBusca('');
    setIaResult(null);
    setIaAtivo(false);
  };

  // ── Opções de categoria derivadas dos dados carregados ──────────────────────
  const categoriaOptions = useMemo(() => {
    const map = new Map();
    for (const c of contas) {
      const temCat = c.categoria_id !== null && c.categoria_id !== undefined;
      const key    = temCat ? String(c.categoria_id) : SEM_CATEGORIA;
      const nome   = temCat
        ? (c.categoria_nome || `Categoria #${c.categoria_id}`)
        : 'Sem categoria';
      if (!map.has(key)) map.set(key, { id: key, nome, count: 0, total: 0 });
      const e = map.get(key);
      e.count += 1;
      e.total += parseFloat(c.valor || 0);
    }
    return [...map.values()].sort(
      (a, b) => b.count - a.count || a.nome.localeCompare(b.nome, 'pt-BR')
    );
  }, [contas]);

  useEffect(() => {
    if (categoriaFiltro && !categoriaOptions.some(o => o.id === categoriaFiltro)) {
      setCategoriaFiltro('');
    }
  }, [categoriaOptions, categoriaFiltro]);

  // contasFiltradas: categoria (client-side) + IA (client-side) sobre o fetch server-side
  const contasFiltradas = useMemo(() => {
    let result = contas;

    // 1. Filtro de categoria (client-side)
    if (categoriaFiltro) {
      if (categoriaFiltro === SEM_CATEGORIA)
        result = result.filter(c => c.categoria_id === null || c.categoria_id === undefined);
      else
        result = result.filter(c => String(c.categoria_id) === categoriaFiltro);
    }

    // 2. Filtro IA (client-side — sobre o resultado da categoria)
    if (iaAtivo && iaResult?.ids?.length) {
      const idSet = new Set(iaResult.ids);
      result = result.filter(c => idSet.has(c.id));
    }

    return result;
  }, [contas, categoriaFiltro, iaAtivo, iaResult]);

  const resumoCategoria = useMemo(() => {
    if (!categoriaFiltro) return null;
    const total = contasFiltradas.reduce((a, c) => a + parseFloat(c.valor || 0), 0);
    return { count: contasFiltradas.length, total };
  }, [categoriaFiltro, contasFiltradas]);

  // ── Helpers de seleção ───────────────────────────────────────────────────────
  const isAutoSupply = (c) =>
    c.origem_tipo === 'manutencao_frota' || c.descricao?.includes('Abastecimento Cartão');

  const toggleRow = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  // Elegível para seleção:
  // - pendentes/vencidas → qualquer perfil (fluxo normal de baixa/aprovação)
  // - pagas → SOMENTE admin_geral (só ele pode excluir/estornar em lote)
  // - aprovadas → nunca (geraram reembolso em A/R; precisa cancelar antes)
  const isSelectable = useCallback((c) => {
    if (c.status === 'aprovado') return false;
    if (c.status === 'pago') return isAdminGeral;
    return true;
  }, [isAdminGeral]);

  const selectableFiltradas = useMemo(
    () => contasFiltradas.filter(isSelectable),
    [contasFiltradas, isSelectable]
  );

  // Mantido para os fluxos de pagamento/aprovação em lote (exclui pagas/aprovadas)
  const payableFiltradas = useMemo(
    () => contasFiltradas.filter(c => c.status !== 'pago' && c.status !== 'aprovado'),
    [contasFiltradas]
  );

  const toggleAll = () => {
    const ids = selectableFiltradas.map(c => c.id);
    const allSel = ids.length > 0 && ids.every(id => selected.has(id));
    const s = new Set(selected);
    if (allSel) ids.forEach(id => s.delete(id));
    else        ids.forEach(id => s.add(id));
    setSelected(s);
  };

  const allChecked  = selectableFiltradas.length > 0 &&
                      selectableFiltradas.every(c => selected.has(c.id));
  const someChecked = selectableFiltradas.some(c => selected.has(c.id)) && !allChecked;

  const totalSelected = contas
    .filter(c => selected.has(c.id))
    .reduce((a, c) => a + parseFloat(c.valor || 0), 0);

  const selectedContas = useMemo(
    () => contas.filter(c => selected.has(c.id)),
    [contas, selected]
  );
  const selectedPagas = useMemo(
    () => selectedContas.filter(c => c.status === 'pago'),
    [selectedContas]
  );

  // ── Handlers de filtro ───────────────────────────────────────────────────────
  const setFilter = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const activeFilterCount =
    [filters.status, filters.origem, categoriaFiltro, filters.pagador_id, filters.numero_nota]
      .filter(Boolean).length;

  const hasSecondaryFilters = !!(
    filters.status || filters.origem || categoriaFiltro || filters.pagador_id || filters.numero_nota
  );

  const clearSecondaryFilters = () => {
    setFilters(f => ({ ...f, status: '', origem: '', pagador_id: '', numero_nota: '' }));
    setCategoriaFiltro('');
  };

  // ── Handlers convencionais ───────────────────────────────────────────────────
  const handleEdit = (conta) => {
    if (isAutoSupply(conta)) { toast.warning('🔒 Registro automático – edição bloqueada.'); return; }
    if (conta.origem_tipo === 'folha_pagamento') { toast.info('Contas de folha são ajustadas no módulo RH.'); return; }
    setEditItem(conta);
    setModalOpen(true);
  };

  const handleDelete = async (conta) => {
  if (!isAdminGeral) { toast.error('Apenas o Admin Geral pode excluir contas.'); return; }

  // Aprovada gerou reembolso — trata em outro fluxo
  if (conta.status === 'aprovado') {
    toast.warn('Esta conta foi aprovada e gerou um recebível de reembolso. Cancele o reembolso antes de excluir.');
    return;
  }

  const isPaga = conta.status === 'pago';
  let msg;
  if (isPaga) {
    msg = isAutoSupply(conta)
      ? `⚠️ "${conta.descricao}" está PAGA e é um registro automático.\n\nAo excluir, o lançamento no caixa será ESTORNADO — pode causar divergência de auditoria. Confirmar?`
      : `"${conta.descricao}" está PAGA.\n\nAo excluir, o lançamento correspondente no caixa será estornado automaticamente. Confirmar?`;
  } else {
    msg = isAutoSupply(conta)
      ? '⚠️ ALERTA: Registro automático. Excluir pode causar divergência. Confirmar?'
      : `Excluir "${conta.descricao}"?`;
  }
  if (!window.confirm(msg)) return;

  try {
    if (isPaga) {
      await api.post(`/api/contas-a-pagar/${conta.id}/estornar-e-excluir`);
      toast.success('Conta estornada e excluída.');
    } else {
      await api.delete(`/api/contas-a-pagar/${conta.id}`);
      toast.success('Conta excluída.');
    }
    reload();
  } catch (e) {
    toast.error(e.response?.data?.error || 'Erro ao excluir.');
  }
};

  // ── Exclusão em lote (admin_geral) ──────────────────────────────────────────
  // Contas pagas selecionadas têm o lançamento de caixa estornado automaticamente
  // pelo backend. Contas aprovadas não podem entrar na seleção (bloqueado acima).
  const handleExcluirLote = async () => {
    if (!isAdminGeral) { toast.error('Apenas o Admin Geral pode excluir contas.'); return; }
    const ids = selectedContas.map(c => c.id);
    if (!ids.length) return;

    const msg = selectedPagas.length > 0
      ? `Excluir ${ids.length} conta(s)?\n\n${selectedPagas.length} está(ão) PAGA(S) — o lançamento correspondente no caixa será ESTORNADO automaticamente para cada uma delas.\n\nConfirmar?`
      : `Excluir ${ids.length} conta(s)?`;
    if (!window.confirm(msg)) return;

    setSubmitting(true);
    try {
      const { data } = await api.post('/api/contas-a-pagar/excluir-lote', { ids });
      toast.success(data?.message || 'Contas processadas com sucesso.');
      if (Array.isArray(data?.ignoradas) && data.ignoradas.length > 0) {
        data.ignoradas.forEach(item => {
          toast.warn(`Conta #${item.id} ignorada: ${item.motivo}`);
        });
      }
      reload();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao excluir contas em lote.');
    } finally {
      setSubmitting(false);
    }
  };

  // [PAGADOR] Núcleo único de baixa
  const pagarContas = useCallback(async (ids, pagadorId = null) => {
    setSubmitting(true);
    try {
      const payload = { ids, ...(pagadorId ? { pagador_id: pagadorId } : {}) };
      const res = await api.post('/api/contas-a-pagar/pagar-lote', payload);
      toast.success(res.data?.message || 'Pagamento registrado!');
      setBaixaModal({ open: false, contas: [] });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao pagar.');
    } finally {
      setSubmitting(false);
    }
  }, [reload]);

  // [PAGADOR] Baixa unitária
  const handlePagarUm = (conta) => {
    if (exibirPagador) {
      setBaixaModal({ open: true, contas: [conta] });
      return;
    }
    if (!window.confirm(`Confirmar pagamento de "${conta.descricao}"?`)) return;
    pagarContas([conta.id]);
  };

  // [PAGADOR] Baixa em lote
  const handlePagarLote = () => {
    const sel = contas.filter(c => selected.has(c.id) && c.status !== 'pago' && c.status !== 'aprovado');
    if (!sel.length) { toast.warn('Nenhuma conta pendente selecionada.'); return; }
    if (exibirPagador) {
      setBaixaModal({ open: true, contas: sel });
      return;
    }
    if (!window.confirm(`Baixar ${sel.length} conta(s)?`)) return;
    pagarContas(sel.map(c => c.id));
  };

  // [PAGADOR] Confirmação vinda da modal
  const handleConfirmBaixa = (pagadorId) => {
    const ids = baixaModal.contas.map(c => c.id);
    if (!ids.length) return;
    pagarContas(ids, pagadorId);
  };

  // [APROVAÇÃO] Aprovar A/P → gera A/R espelho (reembolso ao prestador)
  // Roteia automaticamente: 1 conta → /aprovar, N contas → /aprovar-lote.
  const handleConfirmAprovar = async ({ pagador_id, data_pagamento }) => {
    const contasAlvo = aprovarModal.contas;
    if (!contasAlvo.length) return;
    setSubmitting(true);
    try {
      let data;
      if (contasAlvo.length === 1) {
        const res = await api.post(
          `/api/contas-a-pagar/${contasAlvo[0].id}/aprovar`,
          { pagador_id, data_pagamento }
        );
        data = res.data;
      } else {
        const res = await api.post(
          '/api/contas-a-pagar/aprovar-lote',
          { ids: contasAlvo.map(c => c.id), pagador_id, data_pagamento }
        );
        data = res.data;
      }
      toast.success(data?.message || 'Conta(s) aprovada(s)!');
      setAprovarModal({ open: false, contas: [] });
      reload();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao aprovar conta(s).');
    } finally {
      setSubmitting(false);
    }
  };

  // [PAGADOR-ADMIN] Liga/desliga toggle "Exibir pagador"
  const handleToggleExibirPagador = async () => {
    const novoValor = !exibirPagador;
    setTogglingPag(true);
    setExibirPagador(novoValor); // otimista
    try {
      await api.put('/api/financeiro/config/exibir-pagador', { exibir_pagador: novoValor });
      toast.success(`Exibição de pagador ${novoValor ? 'ativada' : 'desativada'}.`);
    } catch (e) {
      setExibirPagador(!novoValor);
      toast.error(e.response?.data?.error || 'Erro ao alterar configuração.');
    } finally {
      setTogglingPag(false);
    }
  };

  // ── PAGAR VIA CONTA DIGITAL (BaaS Wallet) ───────────────────────────────────
  const handlePagarBaas = async (idsParam = null) => {
    const ids = idsParam ?? [...selected];
    if (!ids.length) { toast.warn('Selecione ao menos uma conta.'); return; }

    const contasSel = contas.filter(c => ids.includes(c.id) && c.status !== 'pago' && c.status !== 'aprovado');
    if (!contasSel.length) { toast.warn('Nenhuma conta pendente selecionada.'); return; }

    const total     = contasSel.reduce((a, c) => a + parseFloat(c.valor), 0);
    const saldoDisp = parseFloat(wallet?.saldo_disponivel || 0);

    if (saldoDisp < total) {
      toast.error(
        `Saldo insuficiente na Conta Digital.\n` +
        `Disponível: ${fmtCurrency(saldoDisp)} | Necessário: ${fmtCurrency(total)}`
      );
      return;
    }

    if (!window.confirm(
      `Pagar ${contasSel.length} conta(s) via Conta Digital?\n\n` +
      `Total a pagar: ${fmtCurrency(total)}\n` +
      `Saldo atual:   ${fmtCurrency(saldoDisp)}\n` +
      `Saldo após:    ${fmtCurrency(saldoDisp - total)}`
    )) return;

    setPayingBaas(true);
    try {
      const { data } = await api.post('/api/baas/pay-bill', {
        ids: contasSel.map(c => c.id),
      });
      toast.success(data.message);
      setWallet(w => w ? { ...w, saldo_disponivel: (saldoDisp - total).toFixed(2) } : null);
      reload();
    } catch (e) {
      toast.error(e.response?.data?.error || 'Erro ao pagar via Conta Digital.');
    } finally {
      setPayingBaas(false);
    }
  };

  // Quantos itens "extras" há no menu (para o badge do botão ☰)
  const menuExtrasCount =
    (isAdminGeral ? 2 : 0) +
    ((exibirPagador || pagadores.length > 0) ? 1 : 0) +
    1; // Lançamento guiado sempre presente

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="cap-root">

      {/* ── HEADER ── */}
      <div className="cap-header">
        <div className="cap-header-left">
          <h1 className="cap-title">Contas a Pagar</h1>

          <div className="cap-kpi-strip">
            {loadingKpi ? (
              <span className="kpi-chip loading"><FaSpinner className="spin-icon" /> carregando…</span>
            ) : (<>
              <button
                className="kpi-chip pending"
                onClick={() => setFilter('status', filters.status === 'pendente' ? '' : 'pendente')}
                title="Filtrar Pendentes"
              >
                <span className="chip-dot" />
                <span className="chip-label">Pendente</span>
                <span className="chip-val">{fmtCurrency(kpis.total_pendente)}</span>
              </button>
              <button
                className="kpi-chip overdue"
                onClick={() => setFilter('status', filters.status === 'vencido' ? '' : 'vencido')}
                title="Filtrar Vencidos"
              >
                <span className="chip-dot" />
                <span className="chip-label">Vencido</span>
                <span className="chip-val">{fmtCurrency(kpis.total_vencido)}</span>
              </button>
              <span className="kpi-chip today">
                <span className="chip-dot" />
                <span className="chip-label">Hoje</span>
                <span className="chip-val">{fmtCurrency(kpis.vence_hoje)}</span>
              </span>
              <span className="kpi-chip paid">
                <span className="chip-dot" />
                <span className="chip-label">Pago/mês</span>
                <span className="chip-val">{fmtCurrency(kpis.pago_mes)}</span>
              </span>
            </>)}
          </div>
        </div>

        <div className="cap-header-actions">
          {wallet && (
            <div className="cap-wallet-badge" title="Saldo da Conta Digital (BaaS)">
              <FaUniversity className="wallet-badge-icon" />
              <div className="wallet-badge-text">
                <span className="wallet-badge-label">Conta Digital</span>
                <strong className="wallet-badge-value">{fmtCurrency(wallet.saldo_disponivel)}</strong>
              </div>
            </div>
          )}

          {/* ── Menu de ações secundárias (☰) ── */}
          <CapActionsMenu count={menuExtrasCount}>
            {(close) => (
              <>
                {/* [PAGADOR-ADMIN] Toggle "Exibir pagador" — exclusivo Admin Geral */}
                {isAdminGeral && (
                  <button
                    type="button"
                    className={`cap-menu-item toggle ${exibirPagador ? 'on' : ''}`}
                    onClick={handleToggleExibirPagador}
                    disabled={togglingPag}
                    title="Exibir seleção de pagador ao dar baixa no caixa"
                  >
                    {togglingPag
                      ? <FaSpinner className="spin-icon" />
                      : (exibirPagador ? <FaToggleOn /> : <FaToggleOff />)}
                    <span>Exibir pagador</span>
                    <span className="cap-menu-item-state">{exibirPagador ? 'Ativo' : 'Inativo'}</span>
                  </button>
                )}

                {/* [PAGADOR-ADMIN] Gestão de pagadores */}
                {isAdminGeral && (
                  <button
                    type="button"
                    className="cap-menu-item"
                    onClick={() => { setGestaoOpen(true); close(); }}
                  >
                    <FaUsers /> <span>Pagadores</span>
                  </button>
                )}

                {/* [PAGADOR] Consolidado */}
                {(exibirPagador || pagadores.length > 0) && (
                  <button
                    type="button"
                    className="cap-menu-item"
                    onClick={() => { setConsolidadoOpen(true); close(); }}
                  >
                    <FaChartBar /> <span>Consolidado por pagador</span>
                  </button>
                )}

                {(isAdminGeral || exibirPagador || pagadores.length > 0) && (
                  <div className="cap-menu-sep" />
                )}

                {/* Lançamento guiado (Wizard) */}
                <button
                  type="button"
                  className="cap-menu-item"
                  onClick={() => { setWizardOpen(true); close(); }}
                >
                  <FaMagic /> <span>Lançamento guiado</span>
                </button>
              </>
            )}
          </CapActionsMenu>

          <RegenteLancamento inline onSuccess={reload} />

          <button className="cap-btn-new" onClick={() => { setEditItem(null); setModalOpen(true); }}>
            <FaPlus /> Nova Conta
          </button>
        </div>
      </div>

      {/* ── FILTER BAR ── */}
      <div className="cap-filter-bar">

        {/* Linha principal: datas + mais filtros */}
        <div className="filter-inline">
          <label>De</label>
          <input type="date" value={filters.dataInicio} onChange={e => setFilter('dataInicio', e.target.value)} />
          <label>Até</label>
          <input type="date" value={filters.dataFim} onChange={e => setFilter('dataFim', e.target.value)} />
          <div className="filter-divider" />
          <button
            className={`filter-more-btn ${filtersOpen ? 'open' : ''}`}
            onClick={() => setFiltersOpen(v => !v)}
          >
            <FaFilter />
            {activeFilterCount > 0 && <span className="filter-badge">{activeFilterCount}</span>}
            Mais filtros
            <FaChevronDown className="chevron" />
          </button>
          {hasSecondaryFilters && (
            <button className="filter-clear-btn" onClick={clearSecondaryFilters}>
              <FaTimes /> Limpar
            </button>
          )}
        </div>

        {/* Filtros expandidos */}
        {filtersOpen && (
          <div className="filter-expanded">
            <div className="filter-field">
              <label>Status</label>
              <select value={filters.status} onChange={e => setFilter('status', e.target.value)}>
                <option value="">Todos</option>
                <option value="pendente">Pendente</option>
                <option value="vencido">Vencido</option>
                <option value="pago">Pago</option>
                <option value="aprovado">Aprovado</option>
              </select>
            </div>

            <div className="filter-field">
              <label>Origem</label>
              <select value={filters.origem} onChange={e => setFilter('origem', e.target.value)}>
                <option value="">Todas</option>
                <option value="despesa_manual">Despesa Manual</option>
                <option value="folha_pagamento">Folha de Pagamento</option>
                <option value="manutencao_frota">Frota / Manutenção</option>
              </select>
            </div>

            <div className="filter-field">
              <label>Categoria Operacional</label>
              <select
                value={categoriaFiltro}
                onChange={e => setCategoriaFiltro(e.target.value)}
                disabled={categoriaOptions.length === 0}
              >
                <option value="">
                  {categoriaOptions.length === 0 ? 'Sem categorias no período' : 'Todas'}
                </option>
                {categoriaOptions.map(c => (
                  <option key={c.id} value={c.id} title={fmtCurrency(c.total)}>
                    {c.nome} ({c.count})
                  </option>
                ))}
              </select>

              {resumoCategoria && (
                <span
                  className="filter-cat-summary"
                  style={{ display: 'block', marginTop: 4, fontSize: '0.78rem', color: '#64748b' }}
                >
                  {resumoCategoria.count} conta{resumoCategoria.count !== 1 ? 's' : ''}
                  {' · '}
                  <strong style={{ color: '#0f1e3d' }}>{fmtCurrency(resumoCategoria.total)}</strong>
                </span>
              )}
            </div>

            {/* [NF] Filtro por número de nota */}
            <div className="filter-field">
              <label>Nº da Nota</label>
              <input
                type="text"
                placeholder="NF-e, NFSe, Recibo..."
                value={filters.numero_nota}
                onChange={e => setFilter('numero_nota', e.target.value)}
                maxLength={100}
              />
            </div>

            {/* [PAGADOR] Filtro por pagador (server-side) */}
            {pagadores.length > 0 && (
              <div className="filter-field">
                <label>Pagador</label>
                <select
                  value={filters.pagador_id}
                  onChange={e => setFilter('pagador_id', e.target.value)}
                >
                  <option value="">Todos</option>
                  {pagadores.map(p => (
                    <option key={p.id} value={p.id}>{p.nome}</option>
                  ))}
                </select>
              </div>
            )}
          </div>
        )}

        {/* ── Linha de busca IA ── */}
        <div className="cap-ia-row">
          <div className="cap-ia-wrap">
            <FaMagic className="cap-ia-icon" />
            <input
              type="text"
              className="cap-ia-input"
              placeholder='Busca inteligente com IA — ex: "despesas vencidas acima de R$500" · "folha de maio" · "nota NF-e 123" · "combustível pendente"'
              value={iaBusca}
              onChange={e => setIaBusca(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleIaBusca()}
            />
            <button
              className="cap-ia-btn"
              onClick={handleIaBusca}
              disabled={iaLoading || !iaBusca.trim()}
            >
              {iaLoading ? <FaSpinner className="spin-icon" /> : 'Buscar com IA'}
            </button>
            {iaAtivo && (
              <button className="cap-ia-clear" onClick={clearIaBusca} title="Limpar busca IA">
                <FaTimes />
              </button>
            )}
          </div>

          {iaAtivo && iaResult && (
            <div className={`cap-ia-banner ${iaResult.total_encontrado === 0 ? 'ia-empty' : 'ia-hit'}`}>
              <FaMagic className="ia-banner-star" />
              <span>
                <strong>{iaResult.total_encontrado}</strong>{' '}
                conta{iaResult.total_encontrado !== 1 ? 's' : ''} encontrada{iaResult.total_encontrado !== 1 ? 's' : ''}
                {iaResult.resumo ? ` · ${iaResult.resumo}` : ''}
              </span>
              <button className="ia-banner-close" onClick={clearIaBusca}><FaTimes /></button>
            </div>
          )}
        </div>
      </div>

      {/* ── TABLE ── */}
      <div className="cap-table-wrap">
        <table className="cap-table">
          <thead>
            <tr>
              <th className="th-id">ID</th>
              <th className="th-date">
                <label className="th-check-label">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    ref={el => { if (el) el.indeterminate = someChecked; }}
                    disabled={selectableFiltradas.length === 0}
                  />
                  <span>Vencimento</span>
                </label>
              </th>
              <th>Descrição</th>
              <th className="th-right">Valor</th>
              <th className="th-center">Status</th>
              <th className="th-origem">Origem</th>
              <th className="th-action">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loadingTable ? (
              <tr><td colSpan={7} className="td-center">
                <FaSpinner className="spin-icon" /> Carregando...
              </td></tr>
            ) : contasFiltradas.length === 0 ? (
              <tr><td colSpan={7} className="td-empty">
                <FaAngleDoubleDown />
                <p>
                  {iaAtivo
                    ? 'IA não encontrou correspondências para essa busca'
                    : categoriaFiltro
                      ? 'Nenhuma conta para a categoria selecionada neste período.'
                      : 'Nenhuma conta encontrada para o período selecionado.'}
                </p>
                {iaAtivo && (
                  <small style={{ color: '#94a3b8', display: 'block', marginTop: 4 }}>
                    Query: &ldquo;{iaBusca}&rdquo;
                  </small>
                )}
              </td></tr>
            ) : contasFiltradas.map(c => {
              const sc         = STATUS_CONFIG[c.status] || { label: c.status, cls: 'pill-pending' };
              const isOverdue  = c.status === 'vencido';
              const isPaid     = c.status === 'pago';
              const isAprovado = c.status === 'aprovado';
              const isAuto     = isAutoSupply(c);
              const isPaidBaas = isPaid && c.forma_pagamento === 'conta_digital';
              const isIaMatch  = iaAtivo && iaResult?.ids?.includes(c.id);

              const { descricaoLimpa } = parseOSTarefa(c.descricao);

              const canPayBaas = wallet &&
                !isPaid &&
                !isAprovado &&
                parseFloat(wallet.saldo_disponivel) >= parseFloat(c.valor);

              return (
                <tr
                  key={c.id}
                  className={[
                    'cap-row',
                    selected.has(c.id) ? 'row-selected' : '',
                    isOverdue  ? 'row-overdue'  : '',
                    isPaidBaas ? 'row-baas-paid' : '',
                    isIaMatch  ? 'row-ia-match' : '',
                  ].filter(Boolean).join(' ')}
                >
                  <td className="td-id">{c.id}</td>

                  <td className="td-date-cell">
                    <label className="date-cell-label">
                      <input
                        type="checkbox"
                        className="row-checkbox"
                        checked={selected.has(c.id)}
                        onChange={() => toggleRow(c.id)}
                        disabled={!isSelectable(c)}
                      />
                      <span className={`date-val ${isOverdue ? 'date-red' : ''} ${isPaid ? 'date-muted' : ''}`}>
                        {fmtDate(c.data_vencimento)}
                      </span>
                      {isOverdue && (
  <span className="overdue-badge" title="Vencido">
    <FaExclamationCircle />
  </span>
)}</label>
                  </td>

                  {/* Descrição + badges */}
                  <td className="td-desc">
                    <div className="desc-inner">
                      {isAuto     && <FaGasPump    className="auto-icon"    title="Automático (abastecimento)" />}
                      {isPaidBaas && <FaUniversity className="baas-icon-sm" title="Pago via Conta Digital BaaS" />}
                      <span className="desc-text" title={c.descricao}>{descricaoLimpa}</span>
                      {c.pagador_nome && (
                        <span className="cap-pagador-tag" title={`Pago por: ${c.pagador_nome}`}>
                          {c.pagador_nome}
                        </span>
                      )}
                    </div>
                    {/* [NF] Badge do número da nota */}
                    {c.numero_nota && (
                      <div className="desc-badges">
                        <span className="nota-badge" title={`Nota fiscal: ${c.numero_nota}`}>
                          <FaFileInvoiceDollar size={9} /> {c.numero_nota}
                        </span>
                      </div>
                    )}
                  </td>

                  <td className="td-valor">{fmtCurrency(c.valor)}</td>

                  <td className="td-status">
                    <span className={`pill ${sc.cls}`}>{sc.label}</span>
                  </td>

                  <td className="td-origem"><OrigemBadge conta={c} /></td>

                  <td className="td-actions">
                    <div className="actions-wrap">
                      {!isPaid && !isAprovado && (
                        <button
                          className="act-btn act-pay"
                          title={isFinanceiro ? 'Aprovar conta' : 'Baixar no caixa'}
                          onClick={() =>
                            isFinanceiro
                              ? setAprovarModal({ open: true, contas: [c] })
                              : handlePagarUm(c)
                          }
                          disabled={submitting || payingBaas}
                        >
                          <FaCheck />
                        </button>
                      )}
                      {!isPaid && !isAprovado && isAdminGeral && (
  <button
    className="act-btn act-convert"
    title="Converter para Conta a Receber"
    onClick={() => setConversaoTarget({ conta: c, tipo: 'ap_to_ar' })}
  >
    <FaExchangeAlt />
  </button>
)}

                      {!isPaid && !isAprovado && wallet && (
                        <button
                          className={`act-btn act-baas ${!canPayBaas ? 'act-disabled' : ''}`}
                          title={
                            canPayBaas
                              ? `Pagar via Conta Digital (saldo: ${fmtCurrency(wallet.saldo_disponivel)})`
                              : 'Saldo insuficiente na Conta Digital'
                          }
                          onClick={() => canPayBaas && handlePagarBaas([c.id])}
                          disabled={!canPayBaas || payingBaas || submitting}
                        >
                          {payingBaas ? <FaSpinner className="spin-icon" /> : <FaUniversity />}
                        </button>
                      )}

                      <button
                        className={`act-btn act-edit ${isAuto ? 'act-disabled' : ''}`}
                        title={isAuto ? 'Bloqueado — registro automático' : 'Editar'}
                        onClick={() => handleEdit(c)}
                      >
                        <FaEdit />
                      </button>
                      
                      {isAdminGeral && (
                        <button className="act-btn act-del" title="Excluir" onClick={() => handleDelete(c)}>
                          <FaTrash />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
                
              );
            })}
          </tbody>

          {/* tfoot com total dos lançamentos visíveis */}
          {contasFiltradas.length > 0 && (
            <tfoot>
              <tr className="tfoot-row">
                <td colSpan={3} className="tfoot-label">
                  {contasFiltradas.length} lançamento{contasFiltradas.length !== 1 ? 's' : ''}
                  {iaAtivo && ' · filtro IA ativo'}
                  {categoriaFiltro && !iaAtivo && ' · categoria filtrada'}
                </td>
                <td className="tfoot-val">
                  {fmtCurrency(contasFiltradas.reduce((a, c) => a + parseFloat(c.valor || 0), 0))}
                </td>
                <td colSpan={3} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* ── MODAL CONVERSÃO ── */}
      {conversaoTarget && (
        <ModalConversaoConta
          conta={conversaoTarget.conta}
          tipo={conversaoTarget.tipo}
          onClose={() => setConversaoTarget(null)}
          onSuccess={() => { setConversaoTarget(null); reload(); }}
        />
      )}

      {/* ── [APROVAÇÃO] MODAL APROVAR CONTA (reembolso → A/R, individual ou em lote) ── */}
      <ModalAprovarConta
        isOpen={aprovarModal.open}
        contas={aprovarModal.contas}
        pagadores={pagadores}
        aprovadorNome={currentUser?.nome}
        submitting={submitting}
        onClose={() => setAprovarModal({ open: false, contas: [] })}
        onConfirm={handleConfirmAprovar}
      />

      {/* ── BATCH BAR (floating) ── */}
      {selected.size > 0 && (
        <div className="cap-batch-bar">
          <div className="batch-info">
            <strong>{selected.size}</strong>
            {' '}conta{selected.size > 1 ? 's' : ''} selecionada{selected.size > 1 ? 's' : ''}
            <span className="batch-total">{fmtCurrency(totalSelected)}</span>
          </div>
          <div className="batch-actions">
            <button
              className="batch-clear"
              onClick={() => setSelected(new Set())}
            >
              <FaTimes /> Limpar
            </button>

            {isFinanceiro ? (
              <button
                className="batch-pay batch-approve"
                onClick={() => {
                  const sel = contas.filter(
                    c => selected.has(c.id) && c.status !== 'pago' && c.status !== 'aprovado'
                  );
                  if (!sel.length) { toast.warn('Nenhuma conta elegível para aprovação selecionada.'); return; }
                  setAprovarModal({ open: true, contas: sel });
                }}
                disabled={submitting || payingBaas}
              >
                {submitting ? <FaSpinner className="spin-icon" /> : <FaCheck />}
                Aprovar selecionadas
              </button>
            ) : (
              <button
                className="batch-pay"
                onClick={handlePagarLote}
                disabled={submitting || payingBaas}
              >
                {submitting ? <FaSpinner className="spin-icon" /> : <FaCheck />}
                Baixar selecionadas
              </button>
            )}

            {wallet && parseFloat(wallet.saldo_disponivel) > 0 && (
              <button
                className={`batch-pay baas ${
                  parseFloat(wallet.saldo_disponivel) < totalSelected ? 'baas-insufficient' : ''
                }`}
                onClick={() => handlePagarBaas()}
                disabled={
                  payingBaas ||
                  submitting ||
                  parseFloat(wallet.saldo_disponivel) < totalSelected
                }
                title={
                  parseFloat(wallet.saldo_disponivel) < totalSelected
                    ? `Saldo insuficiente (${fmtCurrency(wallet.saldo_disponivel)})`
                    : `Pagar via Conta Digital — saldo: ${fmtCurrency(wallet.saldo_disponivel)}`
                }
              >
                {payingBaas ? <FaSpinner className="spin-icon" /> : <FaUniversity />}
                Conta Digital
                {wallet && (
                  <span className="baas-saldo">{fmtCurrency(wallet.saldo_disponivel)}</span>
                )}
              </button>
            )}

            {/* [ADMIN] Exclusão em lote — inclui contas pagas (com estorno automático) */}
            {isAdminGeral && (
              <button
                className="batch-pay batch-delete"
                onClick={handleExcluirLote}
                disabled={submitting || payingBaas}
                title={
                  selectedPagas.length > 0
                    ? `${selectedPagas.length} conta(s) paga(s) — o lançamento no caixa será estornado`
                    : undefined
                }
              >
                {submitting ? <FaSpinner className="spin-icon" /> : <FaTrash />}
                Excluir selecionadas
                {selectedPagas.length > 0 ? ` (${selectedPagas.length} paga${selectedPagas.length > 1 ? 's' : ''})` : ''}
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL ── */}
      {modalOpen && (
        <ModalContasAPagar
          isOpen={modalOpen}
          contaToEdit={editItem}
          onClose={() => setModalOpen(false)}
          onSave={reload}
        />
      )}

      {/* ── WIZARD ── */}
      {wizardOpen && (
        <WizardLancamento
          mode="pagar"
          onClose={() => setWizardOpen(false)}
          onSave={reload}
        />
      )}

      {/* ── [PAGADOR] MODAL DE BAIXA COM PAGADOR ── */}
      <ModalBaixaPagador
        isOpen={baixaModal.open}
        contas={baixaModal.contas}
        pagadores={pagadores}
        submitting={submitting}
        onClose={() => setBaixaModal({ open: false, contas: [] })}
        onConfirm={handleConfirmBaixa}
      />

      {/* ── [PAGADOR-ADMIN] GESTÃO DE PAGADORES ── */}
      {isAdminGeral && (
        <ModalGestaoPagadores
          isOpen={gestaoOpen}
          onClose={() => setGestaoOpen(false)}
          onChange={fetchPagadorSetup}
        />
      )}

      {/* ── [PAGADOR] CONSOLIDADO POR PAGADOR ── */}
      <ModalConsolidadoPagadores
        isOpen={consolidadoOpen}
        onClose={() => setConsolidadoOpen(false)}
      />
    </div>
  );
}